import * as React from "react";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const logoUrl = "/assets/logo.svg";

const navItems = [
  { href: "/", label: "ホーム" },
  { href: "/about", label: "会社概要" },
  { href: "/service", label: "サービス" },
  { href: "/articles", label: "記事" },
] as const;

type HeaderProps = {
  currentPath: string;
  onNavigate: (path: string) => void;
  className?: string;
};

export function Header({ currentPath, onNavigate, className }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleNavigate = (path: string) => {
    setMobileOpen(false);
    onNavigate(path);
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80",
        className,
      )}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            handleNavigate("/");
          }}
          className="flex items-center text-foreground no-underline transition-opacity hover:opacity-80"
          aria-label="ホーム"
        >
          <img src={logoUrl} alt="" className="h-4 w-auto" width={1250} height={137} />
        </a>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex" aria-label="メインナビゲーション">
          {navItems.map(({ href, label }) => {
            const isActive = currentPath === href || (href !== "/" && currentPath.startsWith(href));
            return (
              <a
                key={href}
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigate(href);
                }}
                className={cn(
                  "text-sm tracking-wide text-foreground/80 no-underline transition-colors hover:text-foreground",
                  isActive && "text-foreground font-medium",
                )}
              >
                {label}
              </a>
            );
          })}
        </nav>

        {/* Mobile hamburger button */}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-sm p-2 text-foreground/80 transition-colors hover:text-foreground md:hidden"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label={mobileOpen ? "メニューを閉じる" : "メニューを開く"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <nav
          className="border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden"
          aria-label="モバイルナビゲーション"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-1 px-6 py-4">
            {navItems.map(({ href, label }) => {
              const isActive = currentPath === href || (href !== "/" && currentPath.startsWith(href));
              return (
                <a
                  key={href}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigate(href);
                  }}
                  className={cn(
                    "rounded-sm px-3 py-2.5 text-sm tracking-wide text-foreground/80 no-underline transition-colors hover:bg-muted/20 hover:text-foreground",
                    isActive && "text-foreground font-medium bg-muted/10",
                  )}
                >
                  {label}
                </a>
              );
            })}
          </div>
        </nav>
      )}
    </header>
  );
}
